// Lumoz Console Design Tokens
// Single source of truth for all design values across the application
//
// NOTE: Core palette colors now use CSS variables for theme support.
// CSS variables are injected by ThemeProvider at runtime.
// See src/themes/ for theme definitions.

import { Brain, Hash, Link, Search, BarChart3, BarChartBig, Wrench, Bot, Plug, MessageSquare, Server, Settings, Database, FileText, RotateCw, Circle, AlertCircle, Clock, Shield, ShieldAlert, Network, GitBranch, ScrollText, MessagesSquare, Hammer, FileJson, Files, ArrowLeftRight, FileWarning, Zap, Sparkles, AlertTriangle, Waves, Target } from 'lucide-react';

// Core palette (neutrals, text, accents, status)
// Uses CSS variables for theme support - values are set by ThemeProvider
export const palette = {
  neutrals: {
    canvas: 'var(--color-canvas)',
    panelPrimary: 'var(--color-panel-primary)',
    panelSecondary: 'var(--color-panel-secondary)',
    panelTertiary: 'var(--color-panel-tertiary)',
    panelQuaternary: 'var(--color-panel-quaternary)',
    surfaceHover: 'var(--color-surface-hover)',
    // Semi-transparent surfaces - theme-aware
    surfaceSubtle: 'var(--color-surface-subtle)',
    surfaceMid: 'var(--color-surface-mid)',
    overlayHover: 'var(--color-surface-overlay-hover)',
    optionBg: 'var(--color-input-bg)',
    borderSubtle: 'var(--color-border-subtle)',
    borderStrong: 'var(--color-border-strong)',
  },

  text: {
    heading: 'var(--color-text-heading)',
    primary: 'var(--color-text-primary)',
    secondary: 'var(--color-text-secondary)',
    supporting: 'var(--color-text-supporting)',
    neutral: 'var(--color-text-neutral)',
    neutralLight: 'var(--color-text-neutral)',
    muted: 'var(--color-text-muted)',
    inverse: 'var(--color-text-inverse)',
  },

  accents: {
    primary: {
      base: 'var(--color-accent-primary)',
      vibrant: 'var(--color-accent-primary-vibrant)',
    },
    secondary: {
      base: 'var(--color-accent-secondary)',
      vibrant: 'var(--color-accent-secondary-vibrant)',
    },
    warm: {
      base: 'var(--color-accent-warm)',
      vibrant: 'var(--color-accent-warm-vibrant)',
    },
  },

  // Interactive UI element colors
  interactive: {
    panelIcon: 'var(--color-accent-primary)',  // Icons for opening panels/drawers
  },

  status: {
    danger: 'var(--color-status-danger)',
    warning: 'var(--color-status-warning)',
    success: 'var(--color-status-success)',
    info: 'var(--color-status-info)',
  },

  selection: {
    bg: 'var(--color-selection-bg)',
  },

  support: {
    gridline: 'var(--color-gridline)',
    focusOutline: 'var(--color-focus-outline)',
    attributeLabel: 'var(--color-text-neutral)',
    codeText: 'var(--color-accent-secondary)',
  },

  quality: {
    p20: 'var(--color-quality-p20)',      // Red - Critical (0.0-0.2)
    p40: 'var(--color-quality-p40)',      // Orange - Poor (0.2-0.4)
    p60: 'var(--color-quality-p60)',      // Yellow - Medium (0.4-0.6)
    p80: 'var(--color-quality-p80)',      // Light Green - Good (0.6-0.8)
    p100: 'var(--color-quality-p100)',    // Green - Excellent (0.8-1.0)
    overlayMatte: 'var(--color-overlay)',
    overlayModal: 'var(--color-overlay)',
    shadowCard: 'var(--shadow-card)',
    shadowDrawer: 'var(--shadow-drawer)',
    shadowModal: 'var(--shadow-card)',
    highlight: 'rgba(var(--color-accent-warm-rgb), 0.22)',
  },

  // Border hierarchy for visual separation
  borders: {
    subtle: 'var(--color-border-muted)',
    medium: 'var(--color-border-medium)',
    strong: 'var(--color-border-accent)',
  },

  // Detection severity colors (for prompt injection indicators)
  severity: {
    critical: {
      text: 'var(--color-severity-critical-text)',
      bg: 'var(--color-severity-critical-bg)',
      border: 'var(--color-severity-critical-border)',
      glow: 'var(--color-severity-critical-glow)',
    },
    high: {
      text: 'var(--color-severity-high-text)',
      bg: 'var(--color-severity-high-bg)',
      border: 'var(--color-severity-high-border)',
      glow: 'var(--color-severity-high-glow)',
    },
    medium: {
      text: 'var(--color-severity-medium-text)',
      bg: 'var(--color-severity-medium-bg)',
      border: 'var(--color-severity-medium-border)',
      glow: 'var(--color-severity-medium-glow)',
    },
    low: {
      text: 'var(--color-severity-low-text)',
      bg: 'var(--color-severity-low-bg)',
      border: 'var(--color-severity-low-border)',
      glow: 'var(--color-severity-low-glow)',
    },
    success: {
      text: 'var(--color-severity-success-text)',
      bg: 'var(--color-severity-success-bg)',
      border: 'var(--color-severity-success-border)',
      glow: 'var(--color-severity-success-glow)',
    },
  },

  // Scrollbar styling - theme-aware
  scrollbar: {
    track: 'var(--color-scrollbar-track)',
    thumb: 'var(--color-scrollbar-thumb)',
    thumbHover: 'var(--color-scrollbar-thumb-hover)',
    width: '8px',
    borderRadius: '4px',
  },

};

// Legacy status shortcuts (for existing components)
export const colors = {
  danger: palette.status.danger,
  warning: palette.status.warning,
  success: palette.status.success,
  info: palette.status.info,
  good: palette.status.success,
};

export const withOpacity = (color, percent) =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

// Typography
export const typography = {
  // Font sizes
  size: {
    metadata: 'text-[10px]',     // Table headers, compact metadata
    label: 'text-[11px]',         // Small labels, timestamps, auxiliary info
    xs: 'text-xs',                // Secondary information (12px)
    sm: 'text-sm',                // Body text, standard labels (14px)
    base: 'text-base',            // Default body text (16px)
    xl: 'text-xl',                // Page titles (20px)
    '2xl': 'text-2xl',            // Major headings (24px)
  },

  // Font weights
  weight: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  },

  // Font families
  family: {
    mono: 'font-mono',
  },
};

// Layout & Spacing
export const layout = {
  // Row and item dimensions
  rowHeight: 38,                  // Standard row height for virtualized lists
  indentPerLevel: 28,             // Indentation for nested tree items

  // Timeline & visualization
  timelineWidth: 192,             // Fixed width for timeline column (w-48 = 192px)
  timelineWidthClass: 'w-48',     // Tailwind class equivalent
  spanColumnWidth: 'w-2/5',       // Span column width (40%)

  // Split ratios for two-column layouts
  splitRatio: {
    main: 'w-1/2',                // Primary content area (50%)
    sidebar: 'w-1/2',             // Sidebar/detail area (50%)
  },

  // Grid column templates
  grid: {
    // For span table columns: Kind (35%) | Duration (35%) | Tokens (18%)
    spanColumns: 'grid-cols-[35%_35%_18%]',
    // Cell padding for grid columns
    cellPadding: 'px-2',
    // Overview page grid - 3 equal columns on large screens
    overview: 'grid grid-cols-1 lg:grid-cols-3',
  },
};

// Spacing scale
export const spacing = {
  xs: 'space-y-1',                // 4px - tight spacing
  sm: 'space-y-2',                // 8px - compact spacing
  md: 'space-y-4',                // 16px - standard spacing
  lg: 'space-y-6',                // 24px - section spacing
  xl: 'space-y-8',                // 32px - page-level spacing

  gap: {
    xs: 'gap-1',                  // 4px
    sm: 'gap-2',                  // 8px
    md: 'gap-3',                  // 12px
    lg: 'gap-4',                  // 16px
    xl: 'gap-6',                  // 24px
  },
};

// Component-specific tokens
export const components = {
  section: {
    // Section container - nested sections with visual depth
    container: 'rounded-lg p-4 mb-4',
    bg: 'bg-background-tertiary',
    border: 'border border-border-subtle',

    // Section heading - pure white for emphasis
    titleSize: typography.size.sm,
    titleWeight: typography.weight.semibold,
    titleColor: 'text-text-heading',
    spacing: 'mb-3',

    // Subsection blocks within sections
    subsection: {
      bg: 'bg-background-quaternary',
      border: 'border border-border-subtle',
      padding: 'p-3',
      rounded: 'rounded-lg',
      spacing: 'mb-3',
    },
  },

  badge: {
    padding: 'px-2 py-0.5',
    rounded: 'rounded',
    size: typography.size.xs,
    weight: typography.weight.medium,
  },

  card: {
    rounded: 'rounded-lg',
    border: 'border border-border-subtle',
    bg: 'bg-background-secondary',
    padding: 'p-4',
    titleSpacing: 'mb-4',
    // Interactive card states (for clickable cards)
    interactive: 'cursor-pointer hover:border-border-default transition-colors',
    // Divider within card (separates sections)
    divider: 'border-t border-border-subtle pt-3 mt-3',
    // Inline style properties (for direct style={{}} usage)
    backgroundColor: palette.neutrals.panelSecondary,
    borderColor: palette.borders.subtle,
    borderRadius: '0.5rem',  // 8px = rounded-lg
  },

  button: {
    // Primary action button (Create, Save, Submit, Run, Done)
    primary: 'bg-accent-primary-base text-text-inverse font-medium hover:bg-accent-primary-vibrant transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    // Secondary action button (Cancel, Skip, Close, Discard)
    secondary: 'text-text-secondary font-medium hover:text-text-primary hover:bg-background-tertiary transition-colors',
    // Danger action button (Delete, Remove)
    danger: 'bg-status-danger/20 text-status-danger font-medium hover:bg-status-danger/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  },

  // Page-level section headers (e.g., "Applications", "Workflows")
  sectionHeader: {
    color: palette.text.primary,        // Bright primary text
    fontSize: typography.size.base,     // 16px
    fontWeight: typography.weight.semibold,
    textTransform: 'none',              // Normal case, not uppercase
    letterSpacing: 'normal',
    marginTop: spacing.xl,              // 32px - space above section
    marginBottom: spacing.md,           // 16px - space below header
  },

  pill: {
    // Base pill styling - 3D subtle depth effect
    // Uses CSS variables for theme support (gradient colors defined in themes)
    base: {
      border: 'border border-border-subtle',
      rounded: 'rounded-full',
      padding: 'px-3 py-1.5',
      minHeight: 'min-h-[1.75rem]',
      height: '24px', // 1.5rem = 24px, used for distribution pills without padding (aligns with industry standard)
      // Gradient and shadow applied via inline style for CSS variable support
      gradientStyle: {
        background: 'linear-gradient(to bottom, var(--color-pill-bg-from), var(--color-pill-bg-to))',
        boxShadow: '0 2px 4px rgba(0,0,0,0.15), 0 1px 2px rgba(0,0,0,0.1)',
      },
    },
    // Active state (when filter is toggled on)
    active: {
      border: 'border-2 border-accent-primary-base',
      gradientStyle: {
        background: 'linear-gradient(to bottom, var(--color-pill-active-bg-from), var(--color-pill-active-bg-to))',
        boxShadow: '0 3px 6px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.15)',
      },
    },
    // Hover state for clickable pills (applied via inline style)
    hoverStyle: {
      background: 'linear-gradient(to bottom, var(--color-pill-hover-bg-from), var(--color-pill-hover-bg-to))',
      boxShadow: '0 3px 5px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.12)',
    },
  },

  stats: {
    // Stats bar container (single pill containing multiple stats)
    bar: {
      bg: 'bg-background-secondary',
      border: 'border border-border-subtle',
      rounded: 'rounded-full',
      padding: 'px-3 py-1',
    },
    // Individual stat item
    item: {
      text: 'text-xs text-text-muted',
      spacing: 'whitespace-nowrap',
    },
  },

  pageHeader: {
    // Container for title and time label
    titleContainer: 'flex items-baseline gap-3',
    // Page header metadata (time labels, context info) - uses inline style for Pastel Clay color
    metadataClass: 'text-[11px]',
    metadataColor: 'var(--color-table-time)',
    // Session/tool/model metadata in Traces page
    contextMetadata: 'text-[11px] text-accent-secondary-base',
  },

  table: {
    // Container styling
    container: 'bg-background-secondary rounded-lg border border-border-subtle overflow-hidden h-full flex flex-col',

    // Column widths - Flexible flex-based system
    columnWidth: {
      // ID columns (long text, needs space)
      id: 'flex-[2] min-w-[180px]',

      // Entity columns (medium text)
      entity: 'flex-[1.3] min-w-[130px]',

      // Timestamp columns (medium text)
      timestamp: 'flex-1 min-w-[110px]',

      // Metric columns (short numbers)
      metric: 'flex-[1.61] min-w-[120px]',  // Increased by 0.66 from traceId (workflow column)

      // Count columns (very short numbers)
      count: 'flex-[0.8] min-w-[80px]',

      // List columns (comma-separated values, needs more space)
      list: 'flex-[2.5] min-w-[200px]',

      // Deprecated fixed widths (kept for backwards compatibility during migration)
      traceId: 'flex-[1.34] min-w-[120px]',  // Reduced by 33% (from 2.0 to 1.34, gave 0.66 to metric/workflow)
      session: 'flex-[1.0] min-w-[120px]',  // Reduced from 1.5 (gave 0.5 to quality)
      root: 'flex-1 min-w-[110px]',
      agents: 'flex-[2] min-w-[180px]',
      models: 'flex-[2] min-w-[160px]',
      sessionId: 'flex-[2] min-w-[180px]',
      userId: 'flex-[1.5] min-w-[140px]',
      serviceId: 'flex-[1.5] min-w-[140px]',
      lastEvent: 'flex-1 min-w-[110px]',
      turns: 'flex-[0.6] min-w-[70px]',
      spans: 'flex-[0.6] min-w-[70px]',  // Reduced from 0.8 (gave 0.2 to quality)
      errorSpans: 'flex-[0.8] min-w-[90px]',
      latency: 'flex-1 min-w-[100px]',
      tokens: 'flex-1 min-w-[100px]',
      cost: 'flex-[0.8] min-w-[90px]',
      quality: 'flex-[2.2] min-w-[200px]',  // Increased by 0.7 (0.5 from session + 0.2 from spans)
      flexible: 'flex-1',
    },

    // Cell styling
    cell: {
      padding: 'px-2',
      gap: 'gap-2',                                 // Gap between icon and text in cells
      text: {
        body: 'text-sm text-text-secondary',        // Default text values
        metric: 'text-sm text-text-primary',        // Numeric values (changed to font-normal)
        muted: 'text-sm text-text-muted',
        id: 'font-mono text-sm text-text-primary',
      },
      // Column-based color coding (subtle tints for different data types)
      // Uses CSS variables for theme support
      colors: {
        entity: 'var(--color-table-entity)',      // Primary/entity columns (IDs, names, workflows)
        metric: 'var(--color-table-metric)',      // Numeric values (KPIs, counts, tokens, cost)
        time: 'var(--color-table-time)',          // Quiet supporting tone for timestamps and durations
        // Status colors use semantic palette (danger, warning, success)
      },
    },

    // Header styling
    header: {
      container: 'flex items-center px-4 py-2',  // Complete header row structure
      padding: 'px-2',                           // Individual header cell padding
      gap: 'gap-2',                              // Gap between icon and text in header cells
      text: 'text-xs font-semibold uppercase tracking-[0.14em] text-text-supporting',
      // Modern column-header treatment: one notch below body size, medium
      // weight, muted, slight tracking — header recedes so data reads first.
      columnText: 'text-xs font-medium tracking-[0.05em] text-text-supporting',
      cellPadding: 'px-3 py-3',
      height: 'h-10',                            // Bounded header row height (40px)
      bg: 'bg-[var(--color-table-header-bg)]',
      sticky: 'sticky top-0 z-10',
    },

    // Row styling
    row: {
      height: 38,                                // Matches layout.rowHeight (for virtualization)
      container: 'flex items-center px-4',       // Complete data row structure
      base: 'flex items-center cursor-pointer',  // Legacy - kept for compatibility
      hover: 'hover:bg-background-secondary',
      selected: 'bg-accent-secondary-base/20',
      border: 'border-b border-border-subtle',
      // Selection styling (right border, matches navigation active state)
      selection: {
        borderRight: '2px solid var(--color-accent-secondary)',
        backgroundColor: 'var(--color-nav-item-active-bg)',
      },
    },

    // Resize handle styling (for resizable table columns)
    resizeHandle: {
      width: '3px',
      height: '24px',
      defaultColor: 'var(--color-border-strong)',
      hoverColor: 'var(--color-accent-primary)',
      hoverWidth: '3px',
      transition: 'all 0.2s ease',
    },

    // Empty state
    empty: 'px-4 py-8 text-center text-text-muted',

    // List container (for virtualized content)
    listContainer: 'flex-1',
  },

  // Explore search field (the query surface) — consumed by ExploreSearchField
  searchField: {
    collapsedHeight: 56,      // px, single-line height
    lineHeight: 20,           // px per text line
    maxLines: 3,              // autogrow cap
    fontSize: 14,             // query text
    buttonHeight: 34,         // labeled Search button
    dropdownFontSize: 13,     // recents/examples rows
    sectionLabelSize: 10,     // RECENT / TRY section labels
  },

  // Dropdown menu (time picker, filter selects, etc.)
  dropdown: {
    container: {
      background: palette.neutrals.panelSecondary,
      border: `1px solid ${palette.neutrals.borderStrong}`,
      borderRadius: '0.75rem',        // rounded-xl — matches header selectors
      shadow: palette.quality.shadowCard,
      minWidth: '100px',              // Reduced by 1/3 from 140px
      maxWidth: '120px',
    },
    item: {
      padding: '0.5rem 0.75rem',      // px-3 py-2
      fontSize: typography.size.sm,
      color: {
        default: palette.text.primary,
        selected: palette.accents.primary.base,
      },
      hover: {
        background: palette.neutrals.surfaceHover,
      },
    },
  },

  // Select / input field — matches header dropdown button style
  select: {
    background:   palette.neutrals.panelSecondary,
    border:       `1px solid ${palette.neutrals.borderStrong}`,
    borderRadius: '0.75rem',               // rounded-xl — matches header selectors
    padding:      '0.5rem 0.75rem',        // px-3 py-2
    fontSize:     typography.size.sm,
    color:        palette.text.primary,
    outline:      'none',
    // Hover / focus
    borderFocus:  `1px solid ${palette.accents.primary.base}`,
  },

  // Detection badge (for prompt injection warnings)
  detection: {
    badge: {
      padding: '0.125rem 0.5rem',    // px-2 py-0.5
      borderRadius: '9999px',        // rounded-full
      fontSize: typography.size.xs,
      fontWeight: typography.weight.medium,
      gap: '0.25rem',                // gap-1
      background: palette.neutrals.panelSecondary,
      border: palette.borders.medium,
      icon: {
        size: 12,                    // w-3 h-3
      },
    },
  },

  // Tab styling (for page navigation tabs)
  tab: {
    bg: 'var(--color-panel-tertiary)',
    activeBg: palette.accents.primary.base,
    text: palette.text.primary,                   // Primary text for inactive (readable)
    activeText: palette.text.inverse,             // White text for active tab
    border: 'var(--color-border-strong)',         // Strong border for inactive tabs
    activeBorder: palette.accents.primary.base,
    activeGlow: '0 0 12px var(--color-focus-outline)',

    // Underline tab variant — for sub-section tabs within a panel (e.g. Simulate / Validate)
    underline: {
      track: palette.borders.subtle,              // Divider line below the tab row
      fontSize: 10,
      fontWeightActive: 700,
      fontWeightInactive: 500,
      letterSpacing: '0.07em',
      textTransform: 'uppercase',
      padding: '6px 16px',
      gap: 5,
      colorActive: palette.text.primary,
      colorInactive: palette.text.muted,
      borderActive: `2px solid ${palette.accents.primary.base}`,
      borderInactive: '2px solid transparent',
      marginBottom: -1,                           // Overlap the track line so active border sits flush
    },
  },
};

// Animation & Transitions
export const transitions = {
  colors: 'transition-colors',
  all: 'transition-all',
  fast: 'duration-150',
  normal: 'duration-200',
  slow: 'duration-300',
};

// Opacity levels
export const opacity = {
  disabled: 'opacity-50',
  muted: 'opacity-60',
  subtle: 'opacity-80',
  overlay: 'opacity-10',
};

// Icon sizes and styling
export const iconSize = {
  dot: 'w-2 h-2',                     // Status dots
  xs: 'w-3 h-3',                      // KPI icons, small inline icons
  sm: 'w-4 h-4',                      // Standard UI icons (expand/collapse, actions)
  md: 'w-5 h-5',                      // Medium icons (buttons, toolbars)
  lg: 'w-6 h-6',                      // Large icons (tree expand buttons, avatars)
  xl: 'w-8 h-8',                      // Extra large icons (loading spinners, placeholders)
  xxl: 'w-16 h-16',                   // Hero icons (empty states, onboarding)
};

// Icon shape
export const iconShape = {
  dot: 'rounded-full',                // Circular dots (status indicators)
};

// KPI icons (for key performance indicators)
export const kpiIcons = {
  errorRate: AlertCircle,             // Error Rate metric
  latency: Clock,                     // Latency metric
  security: Shield,                   // Security metric
  dataExfiltration: ShieldAlert,      // Data Exfiltration metric

  // Overview KPI dimensions
  performance: Zap,                   // Performance dimension
  quality: Sparkles,                  // Quality dimension
  reliability: AlertTriangle,         // Reliability dimension
  resiliency: Waves,                  // Resiliency dimension
  optimization: Target,               // Optimization dimension
};

// Graph visualization
export const graph = {
  background: 'var(--color-graph-bg)',

  // Node type definitions with icons and colors
  nodes: {
    // Entity nodes (observable operations) - solid borders
    entity: {
      // OpenInference span kinds
      llm: { stroke: '#D4C4F9', icon: Brain, label: 'LLM' },
      embedding: { stroke: '#B8D4FF', icon: Hash, label: 'EMBEDDING' },
      chain: { stroke: '#A8E6CF', icon: Link, label: 'CHAIN' },
      retriever: { stroke: '#FFD6A5', icon: Search, label: 'RETRIEVER' },
      reranker: { stroke: '#FFAAA5', icon: BarChart3, label: 'RERANKER' },
      tool: { stroke: '#E6C787', icon: Wrench, label: 'TOOL' },
      agent: { stroke: '#C8A58C', icon: Bot, label: 'AGENT' },

      // Forward compatibility fallback for unspecified span kinds
      unspecified: { stroke: '#9E9E9E', icon: Circle, label: 'UNSPECIFIED' },

      // Lumoz extended types
      mcp: { stroke: '#D2B48C', icon: Plug, label: 'MCP' },
      session: { stroke: '#F8BBD9', icon: MessageSquare, label: 'SESSION' },
      service: { stroke: '#8FB39A', icon: Server, label: 'SERVICE' },
    },

    // Concept nodes (configuration/attributes) - dashed borders
    concept: {
      framework: { stroke: '#D4C4F9', icon: Settings, label: 'FRAMEWORK' },
      memory: { stroke: '#D4C4F9', icon: Database, label: 'MEMORY' },
      system_prompt: { stroke: '#D4C4F9', icon: FileText, label: 'SYSTEM PROMPT' },
      turns: { stroke: '#F8BBD9', icon: RotateCw, label: 'TURNS' },
    },

    // Shared node dimensions and styling
    dimensions: {
      width: 136,
      height: 36,
      borderRadius: 10.5,
      borderWidth: 1.25,
      innerGap: '2px',
      padding: '0 8px',
    },

    text: {
      primary: 'var(--color-graph-node-text)',
      secondary: 'var(--color-graph-node-text)',
      fontSize: {
        label: '8px',
        type: '7.5px',
      },
      fontWeight: {
        label: '600',
        type: '400',
      },
      spacing: {
        labelMarginTop: '1.5px',
        labelPaddingLeft: '12px',
        labelPaddingRight: '4px',
        labelMaxWidth: '110px',
        typeMarginTop: '3px',
        typeOpacity: 0.8,
      },
    },

    icon: {
      size: 10.5,
      strokeWidth: 1.5,
      top: 6,
      left: 9,
      fontSize: 10.5,
    },

    background: 'var(--color-graph-node-bg)',

    // Node states
    states: {
      default: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
      },
      mainAgent: (strokeColor) => ({
        boxShadow: `0 0 15px ${strokeColor}, 0 0 30px ${strokeColor}40`,
      }),
      selected: {
        boxShadow: '0 0 15px var(--color-accent-warm), 0 0 30px var(--color-accent-warm)',
      },
      selectedWithBadge: {
        boxShadow: '0 0 8px var(--color-accent-warm), 0 0 15px var(--color-accent-warm)',
      },
      danger: {
        boxShadow: '0 0 15px var(--color-status-danger), 0 0 30px var(--color-status-danger)',
        borderWidth: '2px',
        borderColor: 'var(--color-status-danger)',
      },
    },
  },

  // Security badges for showing security findings on nodes (pastel red)
  badges: {
    securityBadges: {
      size: 16,
      borderRadius: '50%',
      borderColor: 'var(--color-severity-critical-text)',
      iconColor: 'var(--color-severity-critical-text)',
      inactive: {
        borderWidth: '1px',
        background: 'var(--color-graph-node-bg)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      },
      active: {
        borderWidth: '2px',
        background: 'var(--color-panel-tertiary)',
        boxShadow: 'var(--shadow-dropdown)',
      },
      transition: 'all 0.2s ease',
      // Input badges positioned at top of node (detections on system/user messages)
      inputBadges: {
        top: -24,
        horizontalSpacing: 18,
      },
      // Output badges positioned at bottom of node (detections on assistant messages)
      outputBadges: {
        bottom: -24,
        horizontalSpacing: 18,
      },
      iconSize: 12,
      types: {
        prompt_injection: { icon: ShieldAlert, label: 'Prompt Injection' },
        data_exfiltration: { icon: FileWarning, label: 'Data Exfiltration' },
      },
    },
  },

  // Edge styling
  edges: {
    // Connection types
    entity: {
      strokeDasharray: 'none',        // Solid line
    },
    concept: {
      strokeDasharray: '5,5',         // Dashed line
    },

    // Stroke widths by throughput
    strokeWidth: {
      small: 1,                       // < 100 operations
      medium: 2,                      // 100-1000 operations
      large: 3,                       // > 1000 operations
    },

    // Colors by status
    colors: {
      normal: 'var(--color-accent-primary)',
      warning: 'var(--color-status-warning)',
      danger: 'var(--color-status-danger)',
    },
  },

  // Controls and overlays
  controls: {
    miniMap: {
      toggle: {
        top: 16,                      // Top spacing for toggle button (px)
        right: 16,                    // Right spacing for toggle button (px)
        offsetY: 52,                  // Vertical offset for minimap below toggle (px)
      },
    },
  },

  // Layout positioning for graph topology
  layout: {
    // Dagre algorithm configuration
    dagre: {
      nodesep: 80,                    // Horizontal spacing between nodes in same rank
      ranksep: 150,                   // Vertical spacing between ranks
      margin: 50,                     // Graph margins (x and y)
    },

    // Custom layout (tighter spacing for vertical hierarchy)
    custom: {
      nodesep: 60,                    // Tighter horizontal spacing
      ranksep: 110,                   // Tighter vertical spacing
    },

    // Y-axis positioning (top-to-bottom hierarchy)
    yPositions: {
      session: -60,                   // Sessions above agent (110px above)
      agent: 50,                      // Main agent center position
      tool: 160,                      // Tools below agent (110px below)
      service: 270,                   // Services below tools (110px below)
      childAgent: 380,                // Child agents at bottom (110px below services)
    },

    // Vertical offsets (distances between node types)
    offsets: {
      sessionAboveAgent: 110,         // Distance from agent to sessions (above)
      turnsAboveSession: 80,          // Distance from session to turns concept node
      conceptRightOfAgent: 250,       // Distance from agent to concept nodes (horizontal)
      conceptVerticalSpacing: 60,     // Vertical spacing between stacked concept nodes
      conceptCenterAdjust: 60,        // Center offset for concept node positioning
    },

    // Horizontal spacing
    spacing: {
      childAgents: 140,               // Horizontal spacing between child agent nodes
    },
  },
};

// Navigation sidebar
export const navigation = {
  sidebar: {
    width: {
      expanded: '10rem',              // w-40 = 160px
      collapsed: '2.5rem',            // w-10 = 40px
    },
    background: 'var(--color-sidebar-bg)',
    border: {
      color: 'var(--color-nav-divider)',
      width: '1px',
      position: 'border-r',
    },
  },
  brand: {
    fontSize: '1.125rem',             // 18px at 16px base
    fontWeight: '700',
    color: 'var(--color-text-primary)',
    padding: '0.5rem',                // p-2
    borderBottom: '1px solid var(--color-nav-divider)',
  },
  section: {
    padding: '0.5rem',                // px-2 py-3
    marginBottom: '1.5rem',           // mb-6
  },
  sectionHeader: {
    text: 'var(--color-nav-section-header)',
    fontSize: '0.6875rem',            // 11px at 16px base
    fontWeight: '600',
    letterSpacing: '0.05em',          // Wide tracking for headers
    textTransform: 'uppercase',
    marginBottom: '0.75rem',          // mb-3
  },
  divider: {
    color: 'var(--color-nav-divider)',
    margin: '1.5rem 0',               // my-6
  },
  item: {
    default: {
      text: 'var(--color-nav-item-text)',
      background: 'transparent',
      fontSize: '0.8125rem',          // 13px at 16px base
      fontWeight: '500',
      letterSpacing: '0.01em',
      padding: '0.5rem 0.75rem',      // px-3 py-2
      borderRadius: '0.25rem',        // rounded
      gap: '0.5rem',                  // gap-2
    },
    hover: {
      text: 'var(--color-nav-item-hover)',
      background: 'var(--color-nav-item-hover-bg)',
    },
    active: {
      text: 'var(--color-nav-item-active)',
      background: 'var(--color-nav-item-active-bg)',
      borderColor: 'var(--color-nav-item-active)',
      borderWidth: '2px',             // border-r-2
    },
    spacing: '0.25rem',               // space-y-1
  },
  icon: {
    default: 'var(--color-nav-icon-default)',
    hover: 'var(--color-nav-icon-hover)',
    active: 'var(--color-nav-icon-active)',
    size: 16,
    chevron: 14,                      // For collapse/expand
  },
  // Navigation page icons (shared between left nav and Overview cards)
  navIcons: {
    overview: BarChart3,
    agents: Bot,
    sessions: MessageSquare,
    traces: Network,
    workflows: GitBranch,
    apps: Shield,
  },
  user: {
    avatar: {
      size: '1.5rem',                 // w-6 h-6
      background: palette.accents.primary.base,
      borderRadius: '9999px',         // rounded-full
    },
    text: {
      name: {
        fontSize: '0.75rem',          // 12px at 16px base
        fontWeight: '500',
        color: palette.text.primary,
      },
      org: {
        fontSize: '0.75rem',          // 12px at 16px base
        color: palette.text.muted,
      },
    },
    menu: {
      background: palette.neutrals.surfaceHover,
      border: `1px solid var(--color-border-accent)`,
      borderRadius: '0.25rem',
      padding: '0.5rem 0.75rem',      // px-3 py-2
      itemFontSize: '0.875rem',       // 14px at 16px base
    },
  },
};

// Error state (for page load failures, not Error Rate KPI)
export const errorState = {
  container: 'text-center py-8',
  title: 'text-lg font-medium text-text-primary mb-2',
  message: 'text-danger',
  wrapper: 'p-6',
};

// Workflow-specific tokens (imported from separate file to avoid bloating this file)
export { workflowTokens } from './workflowTokens';
